/**
 * API client for Dashboard/Reporting endpoints
 */
import { apiGet, apiPost, apiPatch, apiPut, apiDelete } from '@/lib/api/axios';
import { MentionData } from 'src/hooks/useMentionUsers';
import type { DashboardTileStyle } from '../hooks/useDashboardTileStyle';
import type {
  AccessOverviewResponse,
  MetricNode,
  MetricNodeListResponse,
  MetricNodeCreate,
  MetricNodeUpdate,
  CalculatedDashboardResponse,
  DashboardSummaryResponse,
  DashboardPermissionsResponse,
  GroupDashboardsResponse,
  ShareDashboardRequest,
  SharedUser,
  LevelBoundariesResponse,
  LevelBoundariesUpsert,
  DataValue,
  DataValueListResponse,
  DataValueUpsert,
  BaseValue,
  BaseValueListResponse,
  BaseValueUpsert,
  LinkableCardsResponse,
  LinkedDashboardsResponse,
  AvailableParentsResponse,
  DuplicateCardResponse,
  NodeAuditLogResponse,
  CommentResponse,
  NodeCommentsResponse,
  NodeCommentCountsResponse,
  ImportLimitsResponse,
  ReminderSettings,
  ReminderSettingsUpdate,
  CardReminderOverride
} from './types';

const BASE_PATH = '/dashboards';

// ==================== Dashboards (Root-level GROUP_CARDs) ====================

export async function getDashboards(): Promise<MetricNodeListResponse> {
  const response = await apiGet(`${BASE_PATH}/dashboards`);
  return response.data;
}

export async function getLinkableCards(): Promise<LinkableCardsResponse> {
  const response = await apiGet(`${BASE_PATH}/linkable-cards`);
  return response.data;
}

// ==================== Group Dashboards ====================

export async function getGroupDashboards(groupId: string): Promise<GroupDashboardsResponse> {
  const response = await apiGet(`${BASE_PATH}/groups/${groupId}/dashboards`);
  return response.data;
}

export async function calculateDashboard(
  dashboardId: string,
  periodYear?: number,
  periodMonth?: number,
  numHistoricalMonths?: number,
  periodWeek?: number,
  periodGrain?: 'week' | 'month' | 'quarter',
  tileStyle?: DashboardTileStyle
): Promise<CalculatedDashboardResponse> {
  const params = new URLSearchParams();
  if (periodYear) params.append('period_year', String(periodYear));
  if (periodMonth) params.append('period_month', String(periodMonth));
  if (periodWeek) params.append('period_week', String(periodWeek));
  if (periodGrain) params.append('period_grain', periodGrain);
  if (numHistoricalMonths) params.append('num_historical_months', String(numHistoricalMonths));
  if (tileStyle) params.append('tile_style', tileStyle);

  const queryString = params.toString();
  const url = `${BASE_PATH}/dashboards/${dashboardId}/calculate${queryString ? `?${queryString}` : ''}`;

  const response = await apiGet(url);
  return response.data;
}

function groupSummaryUrl(
  groupId: string,
  suffix: string,
  periodYear?: number,
  periodMonth?: number,
  numHistoricalMonths?: number,
  periodWeek?: number,
  periodGrain?: 'week' | 'month' | 'quarter'
): string {
  const params = new URLSearchParams();
  if (periodYear) params.append('period_year', String(periodYear));
  if (periodMonth) params.append('period_month', String(periodMonth));
  if (periodWeek) params.append('period_week', String(periodWeek));
  if (periodGrain) params.append('period_grain', periodGrain);
  if (numHistoricalMonths) params.append('num_historical_months', String(numHistoricalMonths));

  const queryString = params.toString();
  return `${BASE_PATH}/nodes/${groupId}/summary${suffix}${queryString ? `?${queryString}` : ''}`;
}

/** Read the stored AI summary for a group. Never generates one. */
export async function getGroupSummary(
  groupId: string,
  periodYear?: number,
  periodMonth?: number,
  numHistoricalMonths?: number,
  periodWeek?: number,
  periodGrain?: 'week' | 'month' | 'quarter'
): Promise<DashboardSummaryResponse> {
  const response = await apiGet(
    groupSummaryUrl(groupId, '', periodYear, periodMonth, numHistoricalMonths, periodWeek, periodGrain)
  );
  return response.data;
}

/** Generate a new AI summary and store it. Always calls the LLM. */
export async function refreshGroupSummary(
  groupId: string,
  periodYear?: number,
  periodMonth?: number,
  numHistoricalMonths?: number,
  periodWeek?: number,
  periodGrain?: 'week' | 'month' | 'quarter'
): Promise<DashboardSummaryResponse> {
  const response = await apiPost(
    groupSummaryUrl(groupId, '/refresh', periodYear, periodMonth, numHistoricalMonths, periodWeek, periodGrain),
    {}
  );
  return response.data;
}

// ==================== Metric Nodes ====================

export async function getMetricNode(nodeId: string): Promise<MetricNode> {
  const response = await apiGet(`${BASE_PATH}/nodes/${nodeId}`);
  return response.data;
}

export async function createMetricNode(data: MetricNodeCreate): Promise<MetricNode> {
  const response = await apiPost(`${BASE_PATH}/nodes`, data);
  return response.data;
}

export async function updateMetricNode(nodeId: string, data: MetricNodeUpdate): Promise<MetricNode> {
  const response = await apiPatch(`${BASE_PATH}/nodes/${nodeId}`, data);
  return response.data;
}

export async function deleteMetricNode(nodeId: string): Promise<void> {
  await apiDelete(`${BASE_PATH}/nodes/${nodeId}`);
}

// ==================== Level Boundaries ====================

export async function getLevelBoundaries(nodeId: string): Promise<LevelBoundariesResponse> {
  const response = await apiGet(`${BASE_PATH}/nodes/${nodeId}/boundaries`);
  return response.data;
}

export async function upsertLevelBoundaries(
  nodeId: string,
  data: LevelBoundariesUpsert
): Promise<LevelBoundariesResponse> {
  const response = await apiPut(`${BASE_PATH}/nodes/${nodeId}/boundaries`, data);
  return response.data;
}

// ==================== Data Values ====================

export async function getDataValues(nodeId: string): Promise<DataValueListResponse> {
  const response = await apiGet(`${BASE_PATH}/nodes/${nodeId}/data`);
  return response.data;
}

export async function upsertDataValue(nodeId: string, data: DataValueUpsert): Promise<DataValue> {
  const response = await apiPost(`${BASE_PATH}/nodes/${nodeId}/data`, data);
  return response.data;
}

export async function deleteDataValue(nodeId: string, dataValueId: string): Promise<void> {
  await apiDelete(`${BASE_PATH}/nodes/${nodeId}/data/${dataValueId}`);
}

export async function batchUpsertDataValues(
  values: Array<{
    metric_node_id: string;
    period_year: number;
    period_month?: number | null;
    period_week?: number | null;
    value: number;
  }>
): Promise<{
  created_count: number;
  updated_count: number;
  total_count: number;
  error_count?: number;
  results?: Array<{
    metric_node_id: string;
    period_year: number;
    period_month: number | null;
    period_week: number | null;
    ok: boolean;
    error?: string | null;
  }>;
}> {
  const response = await apiPost(`${BASE_PATH}/data/batch-upsert`, { values });
  return response.data;
}

// ==================== Sharing ====================

export async function getNodePermissions(nodeId: string): Promise<DashboardPermissionsResponse> {
  const response = await apiGet(`${BASE_PATH}/nodes/${nodeId}/permissions`);
  return response.data;
}

export async function shareNode(nodeId: string, data: ShareDashboardRequest): Promise<DashboardPermissionsResponse> {
  const response = await apiPost(`${BASE_PATH}/nodes/${nodeId}/shares`, data);
  return response.data;
}

export async function revokeNodeShare(nodeId: string, permissionId: string): Promise<void> {
  await apiDelete(`${BASE_PATH}/nodes/${nodeId}/shares/${permissionId}`);
}

export async function revokeNodeGroupShare(nodeId: string, groupId: string): Promise<void> {
  await apiDelete(`${BASE_PATH}/nodes/${nodeId}/group-shares/${groupId}`);
}

export async function updateNodeShare(
  nodeId: string,
  permissionId: string,
  data: { permission: number }
): Promise<SharedUser> {
  const response = await apiPatch(`${BASE_PATH}/nodes/${nodeId}/shares/${permissionId}`, data);
  return response.data;
}

// ==================== Base Values ====================

export async function getBaseValues(nodeId: string): Promise<BaseValueListResponse> {
  const response = await apiGet(`${BASE_PATH}/nodes/${nodeId}/base-values`);
  return response.data;
}

export async function upsertBaseValue(nodeId: string, data: BaseValueUpsert): Promise<BaseValue> {
  const response = await apiPost(`${BASE_PATH}/nodes/${nodeId}/base-values`, data);
  return response.data;
}

export async function deleteBaseValue(nodeId: string, baseValueId: string): Promise<void> {
  await apiDelete(`${BASE_PATH}/nodes/${nodeId}/base-values/${baseValueId}`);
}

// ==================== Linked Dashboards ====================

export async function getLinkedDashboards(nodeId: string): Promise<LinkedDashboardsResponse> {
  const response = await apiGet(`${BASE_PATH}/nodes/${nodeId}/linked-dashboards`);
  return response.data;
}

// ==================== Move Card (Available Parents) ====================

export async function getAvailableParents(nodeId: string): Promise<AvailableParentsResponse> {
  const response = await apiGet(`${BASE_PATH}/nodes/${nodeId}/available-parents`);
  return response.data;
}

// ==================== Duplicate Card ====================

export async function duplicateCard(nodeId: string): Promise<DuplicateCardResponse> {
  const response = await apiPost(`${BASE_PATH}/nodes/${nodeId}/duplicate`, {});
  return response.data;
}

// ==================== Import/Export ====================

export async function downloadExportTemplate(nodeId: string, periodYear: number, periodMonth: number): Promise<Blob> {
  const params = new URLSearchParams({
    period_year: String(periodYear),
    period_month: String(periodMonth)
  });
  const url = `${BASE_PATH}/nodes/${nodeId}/export-template?${params.toString()}`;

  const response = await apiGet(url, { responseType: 'blob' });
  return response.data;
}

export async function getImportLimits(): Promise<ImportLimitsResponse> {
  const response = await apiGet(`${BASE_PATH}/import-limits`);
  return response.data;
}

export async function previewImportData(
  nodeId: string,
  file: File,
  periodYear: number,
  periodMonth: number
): Promise<import('./types').ImportPreviewResponse> {
  const params = new URLSearchParams({
    period_year: String(periodYear),
    period_month: String(periodMonth)
  });
  const url = `${BASE_PATH}/nodes/${nodeId}/import-preview?${params.toString()}`;

  const formData = new FormData();
  formData.append('file', file);

  const response = await apiPost(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
}

// ==================== Activity ====================

export async function getNodeAuditLogs(nodeId: string): Promise<NodeAuditLogResponse> {
  const response = await apiGet(`${BASE_PATH}/nodes/${nodeId}/audit-logs`);
  return response.data;
}

// ==================== Comments ====================

export async function getNodeComments(
  nodeId: string,
  periodYear?: number,
  periodMonth?: number,
  periodWeek?: number,
  periodGrain?: 'week' | 'month' | 'quarter'
): Promise<NodeCommentsResponse> {
  const params = new URLSearchParams();
  if (periodYear !== undefined && periodMonth !== undefined) {
    params.append('period_year', String(periodYear));
    params.append('period_month', String(periodMonth));
    if (periodWeek !== undefined) params.append('period_week', String(periodWeek));
    if (periodGrain) params.append('period_grain', periodGrain);
  }
  const queryString = params.toString();
  const url = `${BASE_PATH}/nodes/${nodeId}/comments${queryString ? `?${queryString}` : ''}`;

  const response = await apiGet(url);
  return response.data;
}

export async function addNodeComment(
  nodeId: string,
  content: string,
  mentions?: MentionData[],
  periodYear?: number,
  periodMonth?: number,
  periodWeek?: number
): Promise<CommentResponse> {
  const payload: Record<string, unknown> = { content };
  if (mentions?.length) {
    payload.mentions = mentions.map((m) => (m.user_id ? { user_id: m.user_id } : { email: m.email }));
  }
  if (periodYear !== undefined && periodMonth !== undefined) {
    payload.period_year = periodYear;
    payload.period_month = periodMonth;
    if (periodWeek !== undefined) payload.period_week = periodWeek;
  }
  const response = await apiPost(`${BASE_PATH}/nodes/${nodeId}/comments`, payload);
  return response.data;
}

export async function replyToComment(
  commentId: string,
  content: string,
  mentions?: MentionData[]
): Promise<CommentResponse> {
  const payload: Record<string, unknown> = { content };
  if (mentions?.length) {
    payload.mentions = mentions.map((m) => (m.user_id ? { user_id: m.user_id } : { email: m.email }));
  }
  const response = await apiPost(`${BASE_PATH}/comments/${commentId}/reply`, payload);
  return response.data;
}

export async function editComment(commentId: string, content: string): Promise<CommentResponse> {
  const response = await apiPatch(`${BASE_PATH}/comments/${commentId}/content`, { content });
  return response.data;
}

export async function deleteComment(commentId: string): Promise<void> {
  await apiDelete(`${BASE_PATH}/comments/${commentId}`);
}

export async function getCommentCounts(
  nodeIds: string[],
  periodYear?: number,
  periodMonth?: number,
  periodWeek?: number,
  periodGrain?: 'week' | 'month' | 'quarter'
): Promise<NodeCommentCountsResponse> {
  const payload: Record<string, unknown> = { node_ids: nodeIds };
  if (periodYear !== undefined && periodMonth !== undefined) {
    payload.period_year = periodYear;
    payload.period_month = periodMonth;
    if (periodWeek !== undefined) payload.period_week = periodWeek;
    if (periodGrain) payload.period_grain = periodGrain;
  }
  const response = await apiPost(`${BASE_PATH}/nodes/comment-counts`, payload);
  return response.data;
}

export async function getAccessOverview(nodeId: string): Promise<AccessOverviewResponse> {
  const response = await apiGet(`${BASE_PATH}/nodes/${nodeId}/access-overview`);
  return response.data;
}

// ==================== Reminders ====================

export async function getDashboardReminders(dashboardId: string): Promise<ReminderSettings> {
  const response = await apiGet(`${BASE_PATH}/${dashboardId}/reminders`);
  return response.data;
}

export async function updateDashboardReminders(
  dashboardId: string,
  data: ReminderSettingsUpdate
): Promise<ReminderSettings> {
  const response = await apiPut(`${BASE_PATH}/${dashboardId}/reminders`, data);
  return response.data;
}

export async function setDashboardReminderMute(dashboardId: string, muted: boolean): Promise<ReminderSettings> {
  const response = await apiPut(`${BASE_PATH}/${dashboardId}/reminders/mute`, { muted });
  return response.data;
}

export async function getCardReminderOverride(cardId: string): Promise<CardReminderOverride> {
  const response = await apiGet(`${BASE_PATH}/cards/${cardId}/reminders`);
  return response.data;
}

export async function setCardReminderOverride(
  cardId: string,
  data: CardReminderOverride
): Promise<CardReminderOverride> {
  const response = await apiPut(`${BASE_PATH}/cards/${cardId}/reminders`, data);
  return response.data;
}
